import {
  initializeApp,
} from 'firebase/app';

import {
  getAnalytics,
  logEvent as firebaseLogEvent,
  setUserProperties,
  isSupported,
  setAnalyticsCollectionEnabled,
} from 'firebase/analytics';

import {
  Environment,
  InsightsRestrictions,
  InsightsOperation,
} from '@unu/ui-enums';

import {
  parseFirebaseTagArgs,
  parseFirebaseTraceArgs,
} from '@unu/ui-common';

import {
  FirebaseWebEventNames,
} from './events';

let debugError = () => {};

const globalConfig = {
  app: undefined,
  analytics: undefined,
  supportedPromise: undefined,
  environment: undefined,
};

const isEnabled = async ({
  timeout = 1000,
} = {}) => {

  try {

    if (!globalConfig.supportedPromise) {
      debugError('Firebase Analytics is not supported.');
      return false;
    }

    return await Promise.race([
      globalConfig.supportedPromise,
      new Promise(resolve => setTimeout(() => resolve(false), timeout)),
    ]);

  } catch (error) {
    debugError('Error while checking if analytics is supported', error);
    return false;
  }
};

const logEvent = ({
  context,
  args,
  operation,
} = {}) => {

  const parseArgs = (operation === InsightsOperation.Error && parseFirebaseTraceArgs) || parseFirebaseTagArgs;

  const {
    parsedArgs,
    failureReason,
  } = parseArgs({
    context,
    args,
    nameMap: FirebaseWebEventNames,
  });

  if (failureReason) {
    debugError(`Invalid arguments for ${(operation === InsightsOperation.Error && 'error') || 'tag'}: ${failureReason}`);
    return;
  }

  // Screen view tagging is done automatically by Firebase web
  if (!parsedArgs) {
    return;
  }

  firebaseLogEvent(globalConfig.analytics, ...parsedArgs);
};

const traceError = ({
  context,
  args,
} = {}) => {

  isEnabled()
    .then(enabled => {

      if (!enabled) {
        return;
      }

      logEvent({
        context,
        args,
        operation: InsightsOperation.Error,
      });
    })
    .catch(error => debugError('Error while logging error event to Firebase', error));
};

const tag = ({
  context,
  args,
} = {}) => {

  isEnabled()
    .then(enabled => {

      if (!enabled) {
        return;
      }

      logEvent({
        context,
        args,
        operation: InsightsOperation.Tag,
      });
    })
    .catch(error => debugError('Error while logging tag event to Firebase', error));
};

const createFirebaseInsights = async ({
  context,
  config,
  debugError: debugErrorPrototype,
} = {}) => {

  if (typeof debugErrorPrototype === 'function') {
    debugError = debugErrorPrototype;
  }

  if (!config || typeof config !== 'object') {
    debugError('Firebase configuration parameter is missing or invalid. Please ensure the Firebase config object is properly provided.');
    return;
  }

  try {

    const supportedPromise = isSupported();

    globalConfig.supportedPromise = supportedPromise;

    const supported = await supportedPromise;

    if (!supported) {

      debugError('Firebase Analytics is not supported on this platform.');
      return;
    }

    if (!globalConfig.app || !globalConfig.analytics) {

      globalConfig.app = initializeApp(config);
      globalConfig.analytics = getAnalytics(globalConfig.app);
      setAnalyticsCollectionEnabled(globalConfig.analytics, true);
    }

    if (globalConfig.environment !== context.environment) {
      globalConfig.environment = context.environment;

      setUserProperties(globalConfig.analytics, {
        environment: context.environment,
      });
    }

    return {
      tag,
      traceError,
    };
  } catch (error) {
    debugError('Error during Firebase initialization:', error);
  }
};

export const FirebaseInsightsInterface = {
  restrictions: {
    [InsightsRestrictions.Environments]: [
      Environment.Local,
      Environment.QA,
      Environment.Production,
    ],
  },
  factory: createFirebaseInsights,
};